import { Controller } from "@hotwired/stimulus"

// Drives the "Primary contact" step of the claim flow.
//
// First asks "Are you the primary contact?" via two choice cards. Picking one
// reveals the form:
//   self  → collect only a phone number (name/email come from the signed-in user)
//   other → collect the contact's full name, email, and phone
// The Continue button stays disabled until the required fields for the chosen
// path are filled.
export default class extends Controller {
  static targets = [
    "choice", "form", "type", "otherFields",
    "name", "email", "phone", "phoneLabel", "submit"
  ]
  static values = {
    phoneLabelSelf: String,
    phoneLabelOther: String
  }

  // The "self" default (selected card, revealed form, phone-only) is rendered
  // server-side; we just sync the submit button's disabled state on load.
  connect() {
    this.validate()
  }

  choose(event) {
    const button = event.currentTarget
    this.choiceTargets.forEach((b) => b.setAttribute("aria-checked", String(b === button)))

    const type = button.dataset.choice // "self" | "other"
    this.typeTarget.value = type
    const isSelf = type === "self"

    // Smoothly expand/collapse the name+email block (CSS .pc-collapsible).
    this.otherFieldsTarget.classList.toggle("pc-open", !isSelf)
    if (this.hasPhoneLabelTarget) {
      this.phoneLabelTarget.textContent = isSelf ? this.phoneLabelSelfValue : this.phoneLabelOtherValue
    }

    this.validate()
  }

  // Phone field accepts digits only — strip anything else as the user types and
  // format into (XXX) XXX-XXXX. Caps at 10 digits.
  formatPhone() {
    const digits = this.phoneTarget.value.replace(/\D/g, "").slice(0, 10)

    let formatted = digits
    if (digits.length > 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    } else if (digits.length > 3) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    } else if (digits.length > 0) {
      formatted = `(${digits}`
    }

    this.phoneTarget.value = formatted
    this.validate()
  }

  validate() {
    const isSelf = this.typeTarget.value === "self"
    const phoneOk = this.hasPhoneTarget && this.phoneTarget.value.trim() !== ""
    const nameOk = this.hasNameTarget && this.nameTarget.value.trim() !== ""
    const emailOk = this.hasEmailTarget && this.emailTarget.value.trim() !== ""

    const ok = isSelf ? phoneOk : nameOk && emailOk && phoneOk
    this.submitTarget.disabled = !ok
  }
}
